import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Pill } from '@components/ui/Pill/Pill';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import { ChevronDownIcon } from '@utils/icons';
import rowStyles from '../../SchedulerPage/components/RowLabels/RowLabels.module.css';
import { GroupRailCells } from '../../SchedulerPage/components/RowLabels/RailCells';
import statusStyles from '../../SchedulerPage/components/_status.module.css';
import { rungClass } from '../../SchedulerPage/lib/ladder';
import type { GroupHeader, IPromiseGroup } from './workPlanModel';
import styles from './workPlanGroupRow.module.css';

export interface IWorkPlanGroupRowProps {
  group: IPromiseGroup;
  /** Off when grouped by profession — the rail drops that column. */
  withProfession: boolean;
  collapsed: boolean;
  onToggle: () => void;
  /** The group's promise rows — not rendered while it is collapsed. */
  children: ReactNode;
}

/** A band's label: the trade's own pill when grouped by profession (the rail
 *  has no profession column then), else the floor, the person, or "No …". */
function GroupLabel({ header }: { header: GroupHeader }) {
  const { t } = useTranslation();
  switch (header.kind) {
    case 'profession':
      return <ProfessionPill profession={header.profession} compact />;
    case 'floor':
      return <span className={rowStyles.groupName}>{header.name}</span>;
    case 'assignee':
      return <span className={rowStyles.groupName}>{header.username}</span>;
    default:
      return <span className={rowStyles.groupName}>{t(`activities.workPlan.group.${header.kind}`)}</span>;
  }
}

/**
 * A group's band, as the Gantt draws a group row: the collapse caret and the
 * label, then the Scheduler's own group cells — the profession (when grouped
 * by it), the rows' mean progress, how many promises — and in the day track
 * how many are delayed or overdue, in those rungs' tones. The flat list
 * (Group by: None) has no band.
 */
export function WorkPlanGroupRow({ group, withProfession, collapsed, onToggle, children }: IWorkPlanGroupRowProps) {
  const { t } = useTranslation();
  if (!group.header) return <>{children}</>;
  return (
    <>
      <GroupBand
        group={group}
        header={group.header}
        withProfession={withProfession}
        collapsed={collapsed}
        onToggle={onToggle}
        summary={t('activities.workPlan.activities', { count: group.rows.length })}
      />
      {!collapsed && children}
    </>
  );
}

interface IGroupBandProps {
  group: IPromiseGroup;
  header: GroupHeader;
  withProfession: boolean;
  collapsed: boolean;
  onToggle: () => void;
  summary: string;
}

function GroupBand({ group, header, withProfession, collapsed, onToggle, summary }: IGroupBandProps) {
  const { t } = useTranslation();
  const late = [
    { rung: 'delayed' as const, count: group.delayed },
    { rung: 'overdue' as const, count: group.overdue },
  ].filter(({ count }) => count > 0);
  return (
    <div className={styles.group}>
      <div className={styles.rail}>
        <button type="button" className={styles.toggle} aria-expanded={!collapsed} onClick={onToggle}>
          <span className={styles.caret} data-collapsed={collapsed}>
            <ChevronDownIcon size={12} />
          </span>
          <GroupLabel header={header} />
        </button>
        {/* No uniform profession to show on a floor / assignee band; grouped by
            profession the column is gone. */}
        <GroupRailCells progress={group.progress} profession={withProfession ? undefined : null} />
      </div>
      {/* The count and the late ones on the day side: the rail's status column
          is a circle wide. */}
      <div className={styles.time}>
        <span className={styles.count}>{summary}</span>
        {late.map(({ rung, count }) => (
          <Pill key={rung} className={statusStyles[rungClass(rung)]}>
            {t(`activities.workPlan.group.${rung}`, { count })}
          </Pill>
        ))}
      </div>
    </div>
  );
}
